/**
 * Schémas de la documentation utilisateur.
 *
 * RÈGLE : aucun schéma ne nomme le moteur de calcul, ses versions ni nos
 * dépôts. Ce qui décrit notre chaîne technique appartient au dépôt de calcul,
 * pas à une page que voit un client.
 *
 * Écrits à la main en SVG plutôt que produits par une bibliothèque : aucune
 * dépendance à charger, ils suivent le thème via les classes Tailwind, et ils
 * se relisent en diff. Une capture d'écran, elle, serait périmée au premier
 * changement d'interface.
 */
import React from 'react';

const Boite = ({ x, y, l = 150, h = 44, titre, sous, accent }) => (
  <g>
    <rect
      x={x} y={y} width={l} height={h} rx="8"
      className={accent ? 'fill-primary-500/15 stroke-primary-500/50' : 'fill-dark-800 stroke-dark-600'}
      strokeWidth="1.5"
    />
    <text x={x + l / 2} y={sous ? y + h / 2 - 3 : y + h / 2 + 5}
          textAnchor="middle" className="fill-dark-100 text-[13px] font-medium">
      {titre}
    </text>
    {sous && (
      <text x={x + l / 2} y={y + h / 2 + 14} textAnchor="middle"
            className="fill-dark-400 text-[11px]">{sous}</text>
    )}
  </g>
);

const Fleche = ({ x1, y1, x2, y2, libelle }) => (
  <g>
    <line x1={x1} y1={y1} x2={x2} y2={y2}
          className="stroke-dark-500" strokeWidth="1.5" markerEnd="url(#pointe)" />
    {libelle && (
      <text x={(x1 + x2) / 2} y={y1 === y2 ? y1 - 8 : (y1 + y2) / 2}
            textAnchor="middle" className="fill-dark-500 text-[10px]">{libelle}</text>
    )}
  </g>
);

const Cadre = ({ hauteur, children, titre }) => (
  <figure className="my-6">
    <svg viewBox={`0 0 720 ${hauteur}`} className="w-full h-auto" role="img" aria-label={titre}>
      <defs>
        <marker id="pointe" markerWidth="8" markerHeight="8" refX="7" refY="3"
                orient="auto" markerUnits="strokeWidth">
          <path d="M0,0 L0,6 L7,3 z" className="fill-dark-500" />
        </marker>
      </defs>
      {children}
    </svg>
    <figcaption className="text-dark-500 text-xs text-center mt-2">{titre}</figcaption>
  </figure>
);

/** Du fichier poussé par l'équipe simulation jusqu'à la carte dans l'interface. */
export const SchemaParcoursCase = () => (
  <Cadre hauteur="150" titre="Un case ajouté par l'équipe simulation remonte seul jusqu'à l'interface">
    <Boite x={10} y={50} l={130} titre="mymdo.json" sous="poussé sur GitHub" />
    <Fleche x1={145} y1={72} x2={195} y2={72} />
    <Boite x={200} y={50} l={130} titre="Contrôle" sous="quelques secondes" />
    <Fleche x1={335} y1={72} x2={385} y2={72} />
    <Boite x={390} y={50} l={130} titre="Catalogue" sous="publié sur S3" />
    <Fleche x1={525} y1={72} x2={575} y2={72} />
    <Boite x={580} y={50} l={130} titre="Interface" sous="carte lançable" accent />
    <text x={265} y={125} textAnchor="middle" className="fill-error-400 text-[11px]">
      un case invalide est écarté ici — les autres passent
    </text>
  </Cadre>
);

/** Ce qui se passe entre le clic et les résultats. */
export const SchemaCycleSimulation = () => (
  <Cadre hauteur="190" titre="Les cinq étapes d'un calcul, telles qu'elles apparaissent dans la page de suivi">
    {[
      ['File d\'attente', '1 à 5 min', 10],
      ['Maillage', '3 à 15 min', 152],
      ['Solveur', 'minutes à heures', 294],
      ['Résultats', 'quelques secondes', 436],
      ['Terminé', '', 578],
    ].map(([titre, sous, x], i) => (
      <React.Fragment key={titre}>
        <Boite x={x} y={60} l={132} titre={titre} sous={sous} accent={i === 4} />
        {i < 4 && <Fleche x1={x + 132} y1={82} x2={x + 150} y2={82} />}
      </React.Fragment>
    ))}
    <text x={360} y={150} textAnchor="middle" className="fill-dark-400 text-[11px]">
      Le test d'un case s'arrête après le maillage : il valide la chaîne, pas la physique.
    </text>
    <text x={360} y={168} textAnchor="middle" className="fill-dark-400 text-[11px]">
      Une simulation lancée depuis l'interface va, elle, jusqu'au bout.
    </text>
  </Cadre>
);
